import React, { useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { FormikProps } from 'formik';
import { Form, Formik } from 'formik';

import DateInputField from 'form-elements/date-input-field';
import TextField from 'form-elements/text-field';

import { createLocale } from 'locales';

import FormCard from 'components-new/card/form-card/form-card';
import Typography from 'components-new/typography/typography';
import Button from 'components-new/button/button';

import OrganisationSettingsPageContainer from 'pages/organization/OrganisationSettingsPage/components/organisation-settings-page-container';

import type { IntegrationProvider } from 'state/remote/remote-organization/organisation-settings/use-organisation-settings-integration';
import { useOrganisationSettingsIntegration } from 'state/remote/remote-organization/organisation-settings/use-organisation-settings-integration';
import type { ApiOrganisationSdbListParams } from 'state/__generated__/tadaahAPI';

import organisationSettingsRoutes, {
  IntegrationSettingsBaseRoute,
} from '../organisation-settings-routes';

import SdbCredentialsSchema from './schemas/edit-sdb-credentials-schema';
import fetchJobsSchema from './schemas/fetch-jobs-schema';

const localize = createLocale('organisationIntegrationSettingsLocales');

const SDBIntegrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams({});
  const fetchJobsRef = useRef<FormikProps<ApiOrganisationSdbListParams>>(null);

  const editApiView = searchParams.get('editApiView');

  const { apiKeyForm, fetchJobsForm, sdbJobs, integrations } =
    useOrganisationSettingsIntegration({
      fetchSdbJobsParams: fetchJobsRef?.current?.values,
    });

  const provider = integrations.providers.find(
    (prov) => prov.name === 'SDB Planning',
  ) as IntegrationProvider;

  const onChangeIntegrationClicked = useCallback(() => {
    setSearchParams({ editApiView: 'true' });
  }, [setSearchParams]);

  const onBackClicked = useCallback(() => {
    navigate(
      IntegrationSettingsBaseRoute +
        organisationSettingsRoutes.sdbIntegration.path,
    );
  }, [navigate]);

  const toggleApiKeyButtonLabel = useMemo(() => {
    if (apiKeyForm.apiKeyFieldType === 'password')
      return localize('showApiKey');
    return localize('hideApiKey');
  }, [apiKeyForm.apiKeyFieldType]);

  return (
    <OrganisationSettingsPageContainer
      title={localize('sdbIntegration')}
      selectedRoute={{ groupName: 'organisation', name: 'integrations' }}
      actionElement={
        !editApiView && (
          <Button
            variant="secondary"
            label={localize('updateIntegration')}
            className="mb-2 mx-2 mt-auto"
            size="medium"
            onClick={onChangeIntegrationClicked}
          />
        )
      }
    >
      <div className="flex flex-col justify-center">
        {editApiView && (
          <div className="w-full max-w-2xl flex-col rounded-xl border overflow-hidden">
            <div className="flex justify-center bg-green-50 text-center border-b">
              <img
                alt={provider.name}
                src={`${window.STATIC_URL}logos/integrationProviders${provider.image}`}
                className="h-8 md:h-10 my-6"
              />
            </div>
            <div className="p-4">
              <div>
                <Typography variant="body-large-bold" as="h1" className="mb-2">
                  {provider.name}
                </Typography>
                <Typography variant="body-large" as="p" className="mb-4">
                  {localize('connectSDBInfoParagraph1')}
                </Typography>
                <Typography
                  variant="body-large"
                  as="p"
                  className="mb-4 whitespace-pre-wrap"
                >
                  {localize('connectSDBInfoParagraph2')}
                </Typography>
              </div>
              <Formik
                validationSchema={SdbCredentialsSchema}
                initialValues={apiKeyForm.initialValues}
                onSubmit={apiKeyForm.onFormSubmit}
                enableReinitialize
              >
                <Form className="flex flex-col gap-4">
                  <TextField
                    name="sdb_account_name"
                    placeholder="Organisatie Account Naam (prefix)"
                    title="SDB Organisatie Account"
                    iconProps={{ icon: 'user' }}
                    className="max-w-sm"
                  />
                  <div className="flex flex-col gap-1">
                    <TextField
                      name="sdb_api_key"
                      placeholder="API Key"
                      title="SDB API Key"
                      iconProps={{ icon: 'key-multiple' }}
                      className="max-w-sm"
                      type={apiKeyForm.apiKeyFieldType}
                    />
                    {apiKeyForm.initialValues.sdb_api_key && (
                      <Button
                        variant="secondary"
                        size="medium"
                        label={toggleApiKeyButtonLabel}
                        onClick={apiKeyForm.toggleApiKeyFieldType}
                        className="mt-auto mr-auto"
                      />
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      label="Connect"
                      className="mr-auto"
                      type="submit"
                      loading={apiKeyForm.isLoading}
                    />
                    {provider.hasValidCredentials && (
                      <Button
                        variant="secondary"
                        label={localize('backToFetchJobs')}
                        onClick={onBackClicked}
                      />
                    )}
                  </div>
                </Form>
              </Formik>
            </div>
          </div>
        )}

        {!editApiView && (
          <div className="w-full md:max-w-[768px]">
            <FormCard
              infoContainer={{
                content: localize('fetchJobsDescription'),
                title: 'SDB',
              }}
            >
              <Formik
                innerRef={fetchJobsRef}
                validationSchema={fetchJobsSchema}
                onSubmit={fetchJobsForm.onFormSubmit}
                initialValues={fetchJobsForm.initialValues}
                validateOnChange={false}
              >
                <Form className="flex flex-col min-h-[100px] gap-2">
                  <div className="flex flex-col sm:flex-row justify-center gap-2">
                    <DateInputField
                      placeholder={localize('from')}
                      name="start_date"
                    />
                    <span className="pt-[8px] hidden sm:block">{' - '}</span>
                    <DateInputField
                      placeholder={localize('to')}
                      name="end_date"
                    />
                  </div>
                  <Button
                    variant="primary"
                    label={localize('fetchJobs')}
                    loading={sdbJobs.isLoading}
                    className="mt-auto mr-auto"
                    labelClassName="mx-auto"
                    type="submit"
                  />
                </Form>
              </Formik>
            </FormCard>
          </div>
        )}
      </div>
    </OrganisationSettingsPageContainer>
  );
};

export default SDBIntegrationPage;
